const DEFAULT_TIME_ZONE = "UTC";

const getTimeZone = (timeZone) => timeZone || DEFAULT_TIME_ZONE;

const getDateFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: getTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

const getHourFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: getTimeZone(timeZone),
    hour: "2-digit",
    hour12: false,
  });

const getTimeFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: getTimeZone(timeZone),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

export const parseUtcDateTime = (value) => {
  if (!value) return null;

  const normalizedValue = String(value).trim();
  if (!normalizedValue) return null;

  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue);
  const isoLikeValue = normalizedValue.includes("T")
    ? normalizedValue
    : normalizedValue.replace(" ", "T");
  const utcValue = hasTimeZone ? isoLikeValue : `${isoLikeValue}Z`;
  const parsedDate = new Date(utcValue);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getLocalDateTimeParts = (
  utcValue,
  timeZone = DEFAULT_TIME_ZONE
) => {
  const parsedDate = parseUtcDateTime(utcValue);
  if (!parsedDate) return { date: "", hour: null };

  const date = getDateFormatter(timeZone).format(parsedDate);
  const hour = Number(getHourFormatter(timeZone).format(parsedDate));

  return {
    date,
    hour: Number.isFinite(hour) ? hour : null,
  };
};

export const getLocalDateOrFallback = (
  utcValue,
  fallbackValue = "",
  timeZone = DEFAULT_TIME_ZONE
) => {
  const { date } = getLocalDateTimeParts(utcValue, timeZone);
  return date || String(fallbackValue ?? "").substring(0, 10);
};

export const getLocalHourOrFallback = (
  utcValue,
  fallbackTimeValue = "",
  timeZone = DEFAULT_TIME_ZONE
) => {
  const { hour } = getLocalDateTimeParts(utcValue, timeZone);
  if (hour !== null) return hour;

  const timePart =
    String(fallbackTimeValue ?? "").split(" ")[1] ??
    String(fallbackTimeValue ?? "");
  const parsedHour = Number(String(timePart).split(":")[0]);
  return Number.isFinite(parsedHour) ? parsedHour : null;
};

export const getLocalTimeOrFallback = (
  utcValue,
  fallbackTimeValue = "",
  timeZone = DEFAULT_TIME_ZONE
) => {
  const parsedDate = parseUtcDateTime(utcValue);
  if (parsedDate) return getTimeFormatter(timeZone).format(parsedDate);

  const timePart =
    String(fallbackTimeValue ?? "").split(" ")[1] ??
    String(fallbackTimeValue ?? "");
  const [hour = "", minute = "", second = ""] = String(timePart).split(":");
  if (!hour || !minute) return "";

  return [hour, minute, second || "00"]
    .map((part) => String(part).padStart(2, "0").slice(0, 2))
    .join(":");
};
